import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import { get, map, size } from "lodash"
import moment from "moment"
import CloseIcon from "@material-ui/icons/Close"
import { MobileBottomNav } from "./MobileBottomNav"
import WhiteLogo from "../assets/images/logo/white_logo.png"
import { getLoggedInUserDisplayName } from "../v2_actions/auth"
import { getInitials } from "../v2_actions/photographer"
import { mobileAllSalesList } from "../v2_actions/all_sales"
import { updateListPagination } from "../orm/orm_list_actions"
import { soldPhotographsList } from "../v2_actions/sold_photographs"
import { CurrencyValue } from "./CurrencyValue"

const PAGE_SIZE = 10

export const MobileSales = () => {
    const [sales, setSales] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [selectedSale, setSelectedSale] = useState(null)
    const [soldPhotos, setSoldPhotos] = useState([])
    const [loadingPhotos, setLoadingPhotos] = useState(false)
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const displayName = getLoggedInUserDisplayName()
    const initials = getInitials(displayName)

    const fetchPage = (pageNum) => {
        setLoading(true)
        if (pageNum === 1) {
            dispatch(mobileAllSalesList.updatePaginationNumItemsPerPage(PAGE_SIZE))
            dispatch(mobileAllSalesList.invalidateList())
        }
        dispatch(updateListPagination(mobileAllSalesList.listKey, { items_per_page: PAGE_SIZE, page: pageNum }))
        dispatch(mobileAllSalesList.invalidateList())
        dispatch(mobileAllSalesList.fetchListIfNeeded(true)).then((results) => {
            if (results && results.length > 0) {
                setSales((prev) => pageNum === 1 ? results : [...prev, ...results])
                setHasMore(results.length === PAGE_SIZE)
            } else {
                setHasMore(false)
            }
            setLoading(false)
        })
    }

    const handleSaleClick = (sale) => {
        setSelectedSale(sale)
        setLoadingPhotos(true)
        setSoldPhotos([])
        dispatch(soldPhotographsList.updateListFilter({ ledger_split: get(sale, "id") }))
        dispatch(soldPhotographsList.invalidateList())
        dispatch(soldPhotographsList.fetchListIfNeeded(true)).then((results) => {
            setSoldPhotos(results || [])
            setLoadingPhotos(false)
        })
    }

    const handleCloseDetail = () => {
        setSelectedSale(null)
        setSoldPhotos([])
    }

    useEffect(() => {
        fetchPage(1)
    }, [])

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <img src={WhiteLogo} alt="bossfotos" className={classes.logo} />
                <div className={classes.avatar}>{initials}</div>
            </div>

            <div className={classes.pageContent}>
                <div className={classes.tableTitle}>Customer Sales</div>
                <div className={classes.tableContainer}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th className={classes.th}>Customer</th>
                                <th className={classes.th}>Gallery</th>
                                <th className={`${classes.th} ${classes.thRight}`}>Photos</th>
                                <th className={`${classes.th} ${classes.thRight}`}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.length === 0 ? (
                                <tr>
                                    <td className={classes.td} colSpan={4}>
                                        <div className={classes.emptyRow}>No sales data</div>
                                    </td>
                                </tr>
                            ) : (
                                map(sales, (sale, index) => (
                                    <tr key={index} onClick={() => handleSaleClick(sale)} style={{ cursor: "pointer" }}>
                                        <td className={classes.td}>{get(sale, "customer_name")}</td>
                                        <td className={classes.td}>{get(sale, "session_name")}</td>
                                        <td className={`${classes.td} ${classes.tdRight}`}>{get(sale, "num_photos_sold")}</td>
                                        <td className={`${classes.td} ${classes.tdRight}`}>
                                            <CurrencyValue
                                                showCents={true}
                                                value={get(sale, "photographer_amount")}
                                                country={get(sale, "photographer_country")}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {hasMore && !loading && (
                    <div
                        className={classes.loadMore}
                        onClick={() => {
                            const nextPage = page + 1
                            setPage(nextPage)
                            fetchPage(nextPage)
                        }}
                    >
                        Load More
                    </div>
                )}
            </div>

            <MobileBottomNav activePage="sales" />

            {selectedSale && (
                <div className={classes.overlay} onClick={handleCloseDetail}>
                    <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <span className={classes.modalTitle}>{get(selectedSale, "session_name")}</span>
                            <CloseIcon className={classes.closeIcon} onClick={handleCloseDetail} />
                        </div>
                        <div className={classes.modalBody}>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Customer</span>
                                <span className={classes.detailValue}>{get(selectedSale, "customer_name")}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Date</span>
                                <span className={classes.detailValue}>{moment(get(selectedSale, "created_at")).format("D MMM YYYY")}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Gallery</span>
                                <span className={classes.detailValue}>{get(selectedSale, "session_name")}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Event</span>
                                <span className={classes.detailValue}>{get(selectedSale, "event_name", "")}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Photos Sold</span>
                                <span className={classes.detailValue}>{get(selectedSale, "num_photos_sold")}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Amount</span>
                                <span className={classes.detailValue}>
                                    <CurrencyValue
                                        showCents={true}
                                        value={get(selectedSale, "photographer_amount")}
                                        country={get(selectedSale, "photographer_country")}
                                    />
                                </span>
                            </div>

                            <div className={classes.photosTitle}>Photos</div>
                            {loadingPhotos && <div className={classes.photosLoading}>Loading photos...</div>}
                            {!loadingPhotos && size(soldPhotos) === 0 && (
                                <div className={classes.photosLoading}>No photographs found</div>
                            )}
                            <div className={classes.photosGrid}>
                                {map(soldPhotos, (photo) => (
                                    <div key={get(photo, "id")} className={classes.photoCard}>
                                        <img
                                            src={get(photo, ["gallery_size_file_info", "download_url"])}
                                            alt={get(photo, "photo_name")}
                                            className={classes.photoImg}
                                        />
                                        <div className={classes.photoName}>{get(photo, "photo_name")}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        fontFamily: "Helvetica, Raleway, sans-serif",
        paddingBottom: "60px",
    },
    header: {
        background: "linear-gradient(135deg, #0d2137 0%, #1e88e5 100%)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: "36px",
        width: "auto",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#1565c0",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: 600,
        border: "2px solid #fff",
    },
    pageContent: {
        padding: "16px",
        flex: 1,
    },
    tableTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
        marginBottom: "12px",
    },
    tableContainer: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    th: {
        backgroundColor: "#1565c0",
        color: "#fff",
        fontSize: "12px",
        fontWeight: 600,
        padding: "10px 12px",
        textAlign: "left",
        whiteSpace: "nowrap",
    },
    thRight: {
        textAlign: "right",
    },
    td: {
        fontSize: "13px",
        fontWeight: 500,
        color: "#333",
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
    },
    tdRight: {
        textAlign: "right",
    },
    emptyRow: {
        textAlign: "center",
        color: "#999",
        padding: "20px 0",
        fontSize: "13px",
    },
    loadMore: {
        textAlign: "center",
        padding: "14px",
        marginTop: "8px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        color: "#1e88e5",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
    },
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navIconActive: {
        fontSize: "24px",
        color: "#1e88e5",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
    navLabelActive: {
        fontSize: "10px",
        color: "#1e88e5",
        fontWeight: 600,
    },
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
    },
    modal: {
        backgroundColor: "#fff",
        borderRadius: "16px",
        width: "100%",
        maxWidth: "400px",
        maxHeight: "85vh",
        overflow: "auto",
    },
    modalHeader: {
        background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
        borderRadius: "16px 16px 0 0",
        padding: "14px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 1,
    },
    modalTitle: {
        color: "#fff",
        fontSize: "14px",
        fontWeight: 600,
        flex: 1,
    },
    closeIcon: {
        color: "#fff",
        fontSize: "22px",
        cursor: "pointer",
    },
    modalBody: {
        padding: "16px",
    },
    detailRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
    },
    detailLabel: {
        fontSize: "13px",
        color: "#666",
        minWidth: "100px",
        flexShrink: 0,
    },
    detailValue: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        flex: 1,
        textAlign: "right",
    },
    photosTitle: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#333",
        borderBottom: "1px solid #e0e0e0",
        paddingBottom: "6px",
        marginBottom: "12px",
        marginTop: "16px",
    },
    photosLoading: {
        textAlign: "center",
        color: "#999",
        fontSize: "13px",
        padding: "12px 0",
    },
    photosGrid: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    photoCard: {
        textAlign: "center",
    },
    photoImg: {
        width: "100%",
        borderRadius: "8px",
        objectFit: "contain",
    },
    photoName: {
        fontSize: "10px",
        color: "#999",
        marginTop: "4px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
})
